// src/components/modals/UserFormFields.jsx
import React from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import { TIMEZONES, LANGUAGES, UNITS } from "./permissionsDefaults";

const Section = ({ title, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      bgcolor: "background.paper",
    }}
  >
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      <Divider />
      {children}
    </Stack>
  </Paper>
);

export default function UserFormFields({
  form,
  onChange,
  showPassword = true,
  requirePassword = true,
  showMobile = true,
  showEmail = true,
  showPreferences = true,
  showBusinessFields = false,
}) {
  return (
    <Stack spacing={2}>
      <Section title="Required">
        <Grid container spacing={2}>
          {/* ✅ Backend: username */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Name"
              value={form.username || ""}
              onChange={(e) => onChange("username", e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Grid>

          {/* ✅ Backend: uniqueId (your User ID) */}
          <Grid item xs={12} md={6}>
            <TextField
              label="User ID"
              value={form.uniqueId || ""}
              onChange={(e) => onChange("uniqueId", e.target.value)}
              required
              fullWidth
              size="small"
              helperText="No spaces. Only a-z, 0-9, dot, underscore, dash."
            />
          </Grid>

          {/* ✅ Backend: email (validator required) */}
          {showEmail && (
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={form.email || ""}
                onChange={(e) => onChange("email", e.target.value)}
                required
                fullWidth
                size="small"
                type="email"
              />
            </Grid>
          )}

          {/* ✅ Backend: mobileNo */}
          {showMobile && (
            <Grid item xs={12} md={6}>
              <TextField
                label="Mobile"
                value={form.mobileNo || ""}
                onChange={(e) => onChange("mobileNo", e.target.value)}
                required
                fullWidth
                size="small"
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
          )}

          {/* ✅ Backend: password */}
          {showPassword && (
            <Grid item xs={12} md={6}>
              <TextField
                label="Enter Password"
                value={form.password || ""}
                onChange={(e) => onChange("password", e.target.value)}
                required={requirePassword}
                fullWidth
                size="small"
                type="password"
              />
            </Grid>
          )}
        </Grid>
      </Section>

      {showPreferences && (
        <Section title="Preferences">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Speed Unit"
                value={form.preferences?.speedUnit || ""}
                onChange={(e) => onChange("preferences.speedUnit", e.target.value)}
                fullWidth
                size="small"
              >
                {UNITS.speed.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Distance Unit"
                value={form.preferences?.distanceUnit || ""}
                onChange={(e) => onChange("preferences.distanceUnit", e.target.value)}
                fullWidth
                size="small"
              >
                {UNITS.distance.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Altitude Unit"
                value={form.preferences?.altitudeUnit || ""}
                onChange={(e) => onChange("preferences.altitudeUnit", e.target.value)}
                fullWidth
                size="small"
              >
                {UNITS.altitude.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Volume Unit"
                value={form.preferences?.volumeUnit || ""}
                onChange={(e) => onChange("preferences.volumeUnit", e.target.value)}
                fullWidth
                size="small"
              >
                {UNITS.volume.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Time Zone"
                value={form.preferences?.timeZone || form.preferences?.timezone || ""}
                onChange={(e) => onChange("preferences.timeZone", e.target.value)}
                fullWidth
                size="small"
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Language"
                value={form.preferences?.language || ""}
                onChange={(e) => onChange("preferences.language", e.target.value)}
                fullWidth
                size="small"
              >
                {LANGUAGES.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Section>
      )}

      {showBusinessFields && (
        <Section title="Dealer Details">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Entity Type"
                value={form.entityType || ""}
                onChange={(e) => onChange("entityType", e.target.value)}
                fullWidth
                size="small"
                placeholder="Individual / Company"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Company / Client Name"
                value={form.clientName || ""}
                onChange={(e) => onChange("clientName", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Contact Person Name"
                value={form.contactPersonName || ""}
                onChange={(e) => onChange("contactPersonName", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Contact Person Phone"
                value={form.contactPersonPhone || ""}
                onChange={(e) => onChange("contactPersonPhone", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Contact Person Email"
                value={form.contactPersonEmail || ""}
                onChange={(e) => onChange("contactPersonEmail", e.target.value)}
                fullWidth
                size="small"
                type="email"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="VAT / GST"
                value={form.vat || ""}
                onChange={(e) => onChange("vat", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="License No"
                value={form.licenseNo || ""}
                onChange={(e) => onChange("licenseNo", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="License Validity"
                value={form.licenseValidity || ""}
                onChange={(e) => onChange("licenseValidity", e.target.value)}
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                value={form.address || ""}
                onChange={(e) => onChange("address", e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Section>
      )}

      <Box />
    </Stack>
  );
}
